import axios from 'axios';

export interface NutritionData {
  calories?: number;        // per 100g
  protein?: number;         // grams per 100g
  carbohydrates?: number;   // grams per 100g
  fat?: number;             // grams per 100g
  fiber?: number;           // grams per 100g
  sodium?: number;          // mg per 100g
  sugar?: number;           // grams per 100g
  servingSize?: string;     // e.g., "100g" or "1 cup"
}

export interface ProductInfo {
  name: string;
  category?: 'fridge' | 'freezer' | 'pantry';
  brand?: string;
  image?: string;
  expirationDays?: number;
  allergens?: string[];
  nutrition?: NutritionData;
}

// Lookup product information from barcode using Open Food Facts API
export async function lookupProduct(barcode: string): Promise<ProductInfo | null> {
  try {
    console.log('Looking up barcode:', barcode);
    
    const response = await axios.get(
      `https://world.openfoodfacts.org/api/v0/product/${barcode}.json`,
      {
        timeout: 35000, // 35 second timeout (longer than our 30s Promise.race timeout)
      }
    );

    console.log('API Response:', response.data);

    if (response.data.status === 1 && response.data.product) {
      const product = response.data.product;
      
      console.log('Product found:', product.product_name);
      
      // Estimate expiration days based on category
      const expirationDays = estimateExpirationDays(product);
      
      // Extract allergens
      const allergens = extractAllergens(product);

      // Extract nutrition data
      const nutrition = extractNutrition(product);

      return {
        name: product.product_name || product.product_name_en || 'Unknown Product',
        category: getCategoryFromProduct(product),
        brand: product.brands || undefined,
        image: product.image_url || undefined,
        expirationDays: expirationDays,
        allergens: allergens,
        nutrition: nutrition,
      };
    }

    console.log('Product not found in database');
    return null;
  } catch (error: any) {
    console.error('Error looking up product:', error);
    console.error('Error details:', error.response?.data || error.message);
    return null;
  }
}

// Extract and format allergens from product data
function extractAllergens(product: any): string[] {
  const allergens: string[] = [];
  
  // Open Food Facts provides allergens in multiple formats
  // 1. allergens_tags array (most reliable)
  if (product.allergens_tags && Array.isArray(product.allergens_tags)) {
    product.allergens_tags.forEach((tag: string) => {
      // Tags come like "en:milk", "en:eggs", "en:gluten"
      const allergen = tag.replace(/^en:/, '').replace(/-/g, ' ');
      // Capitalize first letter
      const formatted = allergen.charAt(0).toUpperCase() + allergen.slice(1);
      if (!allergens.includes(formatted)) {
        allergens.push(formatted);
      }
    });
  }
  
  // 2. allergens field (text description)
  if (product.allergens && typeof product.allergens === 'string') {
    const allergenText = product.allergens.toLowerCase();
    
    // Common allergens to check for
    const commonAllergens = [
      'milk', 'eggs', 'fish', 'shellfish', 'tree nuts', 'peanuts', 
      'wheat', 'soybeans', 'gluten', 'sesame', 'mustard', 'celery',
      'lupin', 'molluscs', 'sulphites', 'lactose'
    ];
    
    commonAllergens.forEach(allergen => {
      if (allergenText.includes(allergen)) {
        const formatted = allergen.charAt(0).toUpperCase() + allergen.slice(1);
        if (!allergens.includes(formatted)) {
          allergens.push(formatted);
        }
      }
    });
  }
  
  return allergens;
}

// Extract nutrition data from product
function extractNutrition(product: any): NutritionData | undefined {
  const nutriments = product.nutriments;

  if (!nutriments) {
    return undefined;
  }

  const nutrition: NutritionData = {};

  // Extract values per 100g (Open Food Facts standard)
  // Energy (convert from kJ to kcal if needed)
  if (nutriments.energy_100g) {
    // If energy is in kJ, convert to kcal (1 kcal = 4.184 kJ)
    nutrition.calories = Math.round(nutriments.energy_100g / 4.184);
  } else if (nutriments['energy-kcal_100g']) {
    nutrition.calories = Math.round(nutriments['energy-kcal_100g']);
  }

  // Macronutrients (in grams per 100g)
  if (nutriments.proteins_100g !== undefined) {
    nutrition.protein = Math.round(nutriments.proteins_100g * 10) / 10; // Round to 1 decimal
  }

  if (nutriments.carbohydrates_100g !== undefined) {
    nutrition.carbohydrates = Math.round(nutriments.carbohydrates_100g * 10) / 10;
  }

  if (nutriments.fat_100g !== undefined) {
    nutrition.fat = Math.round(nutriments.fat_100g * 10) / 10;
  }

  if (nutriments.fiber_100g !== undefined) {
    nutrition.fiber = Math.round(nutriments.fiber_100g * 10) / 10;
  }

  if (nutriments.sugars_100g !== undefined) {
    nutrition.sugar = Math.round(nutriments.sugars_100g * 10) / 10;
  }

  // Sodium (in mg per 100g)
  if (nutriments.sodium_100g !== undefined) {
    nutrition.sodium = Math.round(nutriments.sodium_100g * 1000); // Convert from g to mg
  } else if (nutriments.salt_100g !== undefined) {
    // Convert salt to sodium (sodium = salt / 2.5)
    nutrition.sodium = Math.round((nutriments.salt_100g / 2.5) * 1000);
  }

  // Serving size
  if (product.serving_size) {
    nutrition.servingSize = product.serving_size;
  }

  // Only return if we have at least some nutrition data
  const hasData = nutrition.calories !== undefined ||
                  nutrition.protein !== undefined ||
                  nutrition.carbohydrates !== undefined;

  return hasData ? nutrition : undefined;
}

// Determine which category (fridge/freezer/pantry) based on product data
function getCategoryFromProduct(product: any): 'fridge' | 'freezer' | 'pantry' {
  const categories = product.categories_tags || [];
  const productName = (product.product_name || '').toLowerCase();

  // Check categories for clues
  const categoryString = categories.join(' ').toLowerCase();

  // Frozen foods
  if (
    categoryString.includes('frozen') ||
    productName.includes('frozen') ||
    productName.includes('ice cream')
  ) {
    return 'freezer';
  }

  // Pantry items (dry goods, canned, etc.)
  if (
    categoryString.includes('canned') ||
    categoryString.includes('dried') ||
    categoryString.includes('pasta') ||
    categoryString.includes('rice') ||
    categoryString.includes('cereal') ||
    categoryString.includes('snack') ||
    categoryString.includes('beverage') ||
    categoryString.includes('condiment') ||
    productName.includes('canned') ||
    productName.includes('box') ||
    productName.includes('jar')
  ) {
    return 'pantry';
  }

  // Default to fridge for perishables
  return 'fridge';
}

// Estimate expiration days based on product category
function estimateExpirationDays(product: any): number {
  const categories = product.categories_tags || [];
  const categoryString = categories.join(' ').toLowerCase();
  const productName = (product.product_name || '').toLowerCase();

  // Frozen foods - longer shelf life
  if (categoryString.includes('frozen') || productName.includes('frozen')) {
    return 90; // ~3 months
  }

  // Fresh meat/fish - very short shelf life
  if (
    categoryString.includes('meat') ||
    categoryString.includes('poultry') ||
    categoryString.includes('fish') ||
    categoryString.includes('seafood') ||
    categoryString.includes('chicken') ||
    categoryString.includes('beef') ||
    categoryString.includes('pork') ||
    productName.includes('meat') ||
    productName.includes('chicken') ||
    productName.includes('fish')
  ) {
    return 3; // 3 days
  }

  // Dairy products - varies by type
  if (categoryString.includes('milk') || productName.includes('milk')) {
    return 7; // 1 week for milk
  }
  
  if (categoryString.includes('yogurt') || productName.includes('yogurt')) {
    return 14; // 2 weeks for yogurt
  }
  
  if (categoryString.includes('cheese') || productName.includes('cheese')) {
    // Hard cheese lasts longer
    if (productName.includes('parmesan') || productName.includes('cheddar')) {
      return 30; // 1 month
    }
    return 14; // 2 weeks for soft cheese
  }
  
  if (
    categoryString.includes('dairy') ||
    categoryString.includes('cream') ||
    productName.includes('cream')
  ) {
    return 7; // 1 week
  }

  // Fresh produce - varies by type
  if (
    categoryString.includes('fruits') ||
    categoryString.includes('vegetables') ||
    categoryString.includes('produce') ||
    categoryString.includes('salad')
  ) {
    // Leafy greens spoil faster
    if (
      productName.includes('lettuce') ||
      productName.includes('spinach') ||
      productName.includes('salad') ||
      productName.includes('herbs')
    ) {
      return 3; // 3 days
    }
    
    // Berries spoil faster
    if (
      productName.includes('berry') ||
      productName.includes('strawberry') ||
      productName.includes('raspberry') ||
      productName.includes('blueberry')
    ) {
      return 5; // 5 days
    }
    
    // Root vegetables last longer
    if (
      productName.includes('potato') ||
      productName.includes('carrot') ||
      productName.includes('onion') ||
      productName.includes('garlic')
    ) {
      return 14; // 2 weeks
    }
    
    return 7; // Default for produce
  }

  // Bread and baked goods
  if (
    categoryString.includes('bread') ||
    categoryString.includes('bakery') ||
    productName.includes('bread') ||
    productName.includes('bagel') ||
    productName.includes('muffin')
  ) {
    return 5; // 5 days
  }

  // Eggs
  if (categoryString.includes('egg') || productName.includes('egg')) {
    return 21; // 3 weeks
  }

  // Deli/prepared foods
  if (
    categoryString.includes('deli') ||
    categoryString.includes('prepared') ||
    productName.includes('sandwich') ||
    productName.includes('salad')
  ) {
    return 3; // 3 days
  }

  // Condiments and sauces
  if (
    categoryString.includes('condiment') ||
    categoryString.includes('sauce') ||
    categoryString.includes('dressing') ||
    productName.includes('ketchup') ||
    productName.includes('mayo') ||
    productName.includes('mustard')
  ) {
    return 60; // 2 months after opening
  }

  // Beverages
  if (categoryString.includes('beverage') || categoryString.includes('drink')) {
    // Juice
    if (categoryString.includes('juice') || productName.includes('juice')) {
      return 7; // 1 week after opening
    }
    // Soda
    if (categoryString.includes('soda') || productName.includes('soda')) {
      return 180; // 6 months
    }
    return 14; // 2 weeks default
  }

  // Canned/packaged goods - very long shelf life
  if (
    categoryString.includes('canned') ||
    categoryString.includes('preserved') ||
    categoryString.includes('jar') ||
    productName.includes('canned')
  ) {
    return 365; // 1 year
  }

  // Snacks and dry goods
  if (
    categoryString.includes('snack') ||
    categoryString.includes('chips') ||
    categoryString.includes('crackers') ||
    productName.includes('chips') ||
    productName.includes('crackers')
  ) {
    return 60; // 2 months
  }

  // Pasta and rice
  if (
    categoryString.includes('pasta') ||
    categoryString.includes('rice') ||
    productName.includes('pasta') ||
    productName.includes('rice')
  ) {
    return 365; // 1 year
  }

  // Cereal and breakfast foods
  if (
    categoryString.includes('cereal') ||
    categoryString.includes('breakfast') ||
    productName.includes('cereal')
  ) {
    return 90; // 3 months
  }

  // Default for unknown products
  return 7; // 1 week default
}

// Alternative: UPC Database API (requires API key)
export async function lookupProductUPC(barcode: string, apiKey: string): Promise<ProductInfo | null> {
  try {
    const response = await axios.get(
      `https://api.upcitemdb.com/prod/trial/lookup?upc=${barcode}`,
      {
        headers: {
          'user_key': apiKey,
        },
      }
    );

    if (response.data.items && response.data.items.length > 0) {
      const item = response.data.items[0];
      
      return {
        name: item.title || 'Unknown Product',
        brand: item.brand || undefined,
        image: item.images?.[0] || undefined,
        expirationDays: 7, // Default
      };
    }

    return null;
  } catch (error) {
    console.error('Error looking up product from UPC database:', error);
    return null;
  }
}
